import React from 'react';
import {
  Check,
  X,
  Minus,
  Zap,
  Database,
  Clock,
  Code2,
  Bot,
  TrendingUp,
  AlertTriangle,
  FileText,
  DollarSign,
  BarChart3,
  Globe,
  Layers,
  Search,
  Shield,
  CreditCard,
  RefreshCw,
  Scale,
  Lock,
} from 'lucide-react';
import FAQ from '@/components/landing/pricing/FAQ';
import {
  Section,
  H2,
  H3,
  P,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  IconCell,
  Callout,
  CodeBlock,
  CTALink,
  Chart,
  ChartTitle,
  ChartRow,
  ChartLabel,
  ChartBarTrack,
  ChartBar,
  ChartValue,
  TimelineContainer,
  TimelineRow,
  TimelineLabel,
  TimelineTrack,
  TimelineFill,
  StatGrid,
  StatCard,
  VerdictBox,
} from '@/pages/styles/ComparisonStyles';

export const meta = {
  title: 'Free Stock Data APIs Compared | AxionQuant',
  description:
    'Compare the best free stock data APIs on rate limits, data quality, historical depth, and hidden costs. Find the right free API for your project.',
  canonicalUrl: 'https://axionquant.com/compare/free-stock-data-apis',
};

const Yes = ({ children }: { children?: React.ReactNode }) => (
  <IconCell $label={!!children}>
    <Check size={18} color="#16a34a" strokeWidth={3} />
    {children}
  </IconCell>
);

const No = ({ children }: { children?: React.ReactNode }) => (
  <IconCell $label={!!children}>
    <X size={18} color="#dc2626" strokeWidth={3} />
    {children}
  </IconCell>
);

const Partial = ({ children }: { children?: React.ReactNode }) => (
  <IconCell $label={!!children}>
    <Minus size={18} color="#f59e0b" strokeWidth={3} />
    {children}
  </IconCell>
);

export default function FreeStockDataApisContent() {
  return (
    <>
      <Section>
        <H2 className="serif">Free Stock Data APIs</H2>
        <P>
          Every financial data API advertises a free tier. What almost none of
          them advertise is what the free tier actually lets you build. A free
          plan that caps you at 25 requests per day sounds generous until you
          realize that a single backtest across 30 tickers will take you two
          days to complete.
        </P>
        <P>
          This comparison cuts through the marketing language. It examines the
          free tiers of the leading stock data APIs on the metrics that
          actually determine whether you can build something real: rate
          limits, data quality, historical depth, asset coverage, and the
          hidden costs that show up after you have already written your
          integration code.
        </P>

        <StatGrid>
          <StatCard>
            <div className="value">25</div>
            <div className="label">
              Daily requests on Alpha Vantage free tier, the lowest in this
              comparison
            </div>
          </StatCard>
          <StatCard>
            <div className="value">5</div>
            <div className="label">
              Requests per minute on Polygon.io free tier
            </div>
          </StatCard>
          <StatCard>
            <div className="value">1,000</div>
            <div className="label">
              Daily requests on Tiingo free tier
            </div>
          </StatCard>
          <StatCard>
            <div className="value">$0</div>
            <div className="label">
              Cost to start with AxionQuant, including historical and
              alternative data
            </div>
          </StatCard>
        </StatGrid>
      </Section>

      <Section>
        <H2 className="serif">At a Glance</H2>
        <P>
          The table below summarizes the free tiers of the six leading
          developer-focused stock data APIs. The differences are significant,
          and they compound as your project grows.
        </P>

        <Table>
          <THead>
            <TR>
              <TH>Provider</TH>
              <TH>Free Tier Rate Limit</TH>
              <TH>Daily Cap</TH>
              <TH>Historical OHLCV</TH>
              <TH>Real-Time</TH>
              <TH>Alternative Data</TH>
              <TH>Commercial Use</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>AxionQuant</strong>
              </TD>
              <TD>Generous free allowance</TD>
              <TD>Generous daily budget</TD>
              <TD>
                <Yes>Deep multi-decade</Yes>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes>SEC, insider, ESG, credit, sentiment</Yes>
              </TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Alpha Vantage</strong>
              </TD>
              <TD>5 per minute</TD>
              <TD>25 per day</TD>
              <TD>
                <Yes>20+ years daily</Yes>
              </TD>
              <TD>
                <Partial>15-min delayed</Partial>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial>Contact required</Partial>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Finnhub</strong>
              </TD>
              <TD>60 per minute</TD>
              <TD>No daily cap</TD>
              <TD>
                <No>Not available</No>
              </TD>
              <TD>
                <Partial>15-min delayed</Partial>
              </TD>
              <TD>
                <No>Paid only</No>
              </TD>
              <TD>
                <No>Personal use only</No>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Twelve Data</strong>
              </TD>
              <TD>8 per minute</TD>
              <TD>800 per day</TD>
              <TD>
                <Partial>Limited</Partial>
              </TD>
              <TD>
                <Yes>Real-time</Yes>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial>Limited</Partial>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Polygon.io</strong>
              </TD>
              <TD>5 per minute</TD>
              <TD>No daily cap</TD>
              <TD>
                <Partial>2 years EOD only</Partial>
              </TD>
              <TD>
                <No>15-min delayed</No>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial>Limited</Partial>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Tiingo</strong>
              </TD>
              <TD>50 per hour</TD>
              <TD>1,000 per day</TD>
              <TD>
                <Yes>Decades of EOD</Yes>
              </TD>
              <TD>
                <Partial>Limited</Partial>
              </TD>
              <TD>
                <Partial>News</Partial>
              </TD>
              <TD>
                <No>Personal only</No>
              </TD>
            </TR>
          </TBody>
        </Table>

        <H3>Free Tier Feature Checklist</H3>
        <Table>
          <THead>
            <TR>
              <TH>Capability</TH>
              <TH>AxionQuant</TH>
              <TH>Alpha Vantage</TH>
              <TH>Finnhub</TH>
              <TH>Twelve Data</TH>
              <TH>Polygon.io</TH>
              <TH>Tiingo</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>Historical OHLCV on free tier</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial />
              </TD>
              <TD>
                <Partial />
              </TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>Real-time quotes on free tier</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial />
              </TD>
              <TD>
                <Partial />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial />
              </TD>
            </TR>
            <TR>
              <TD>Alternative data on free tier</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial />
              </TD>
            </TR>
            <TR>
              <TD>Commercial use on free tier</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial />
              </TD>
              <TD>
                <Partial />
              </TD>
              <TD>
                <No />
              </TD>
            </TR>
            <TR>
              <TD>Official SDK</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial />
              </TD>
            </TR>
            <TR>
              <TD>MCP server for AI agents</TD>
              <TD>
                <Yes>100+ tools</Yes>
              </TD>
              <TD>
                <Partial />
              </TD>
              <TD>
                <Yes>70 tools</Yes>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
            </TR>
            <TR>
              <TD>Credit card required</TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
            </TR>
          </TBody>
        </Table>
      </Section>

      <Section>
        <H2 className="serif">Rate Limits and Quotas</H2>
        <P>
          Rate limits are the first constraint you will hit on any free tier.
          The models vary widely: some providers use per-minute limits,
          others use per-day caps, and some use both. The distinction matters
          because a per-minute limit allows bursty workloads, while a per-day
          cap forces you to spread work across multiple days regardless of
          how fast your code runs.
        </P>

        <Callout>
          <strong>The backtest problem:</strong> To backtest a strategy across
          30 stocks for one year of daily data, you need 30 API calls. On
          Alpha Vantage free tier, that is more than your entire daily quota
          of 25. On Polygon.io free tier at 5 calls per minute, you can
          complete the same task in 6 minutes. The same workload that takes
          Alpha Vantage users two days takes Polygon users six minutes.
        </Callout>

        <Chart>
          <ChartTitle>
            <Zap size={16} />
            Free Tier Daily Request Capacity
          </ChartTitle>

          <ChartRow>
            <ChartLabel>Tiingo</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={100} $color="#0066cc" />
            </ChartBarTrack>
            <ChartValue>1,000 / day</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Twelve Data</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={80} $color="#0ea5e9" />
            </ChartBarTrack>
            <ChartValue>800 / day</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>AxionQuant</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={75} $color="#16a34a" />
            </ChartBarTrack>
            <ChartValue>Generous budget</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Alpha Vantage</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={3} $color="#dc2626" />
            </ChartBarTrack>
            <ChartValue>25 / day</ChartValue>
          </ChartRow>
        </Chart>

        <P>
          Finnhub and Polygon.io use per-minute limits without a daily cap,
          which means your total daily capacity depends on how long you are
          willing to let your code run. Finnhub free tier allows 60 requests
          per minute, which at full utilization works out to 86,400 requests
          per day in theory. Polygon.io free tier allows 5 requests per
          minute, which caps at 7,200 requests per day.
        </P>

        <Table>
          <THead>
            <TR>
              <TH>Provider</TH>
              <TH>Per-Minute Limit</TH>
              <TH>Per-Day Limit</TH>
              <TH>Overage Behavior</TH>
              <TH>Burst-Friendly</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>AxionQuant</strong>
              </TD>
              <TD>Header-driven</TD>
              <TD>Generous budget</TD>
              <TD>Headers report remaining budget</TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Alpha Vantage</strong>
              </TD>
              <TD>5</TD>
              <TD>25</TD>
              <TD>Hard cut-off, error payload breaks parsers</TD>
              <TD>
                <No />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Finnhub</strong>
              </TD>
              <TD>60</TD>
              <TD>None</TD>
              <TD>HTTP 429, wait 1 to 2 seconds</TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Twelve Data</strong>
              </TD>
              <TD>8</TD>
              <TD>800</TD>
              <TD>Credits deducted, resets daily</TD>
              <TD>
                <Partial />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Polygon.io</strong>
              </TD>
              <TD>5</TD>
              <TD>None</TD>
              <TD>Hard cap, 429 on exceed</TD>
              <TD>
                <No>5 per minute is slow</No>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Tiingo</strong>
              </TD>
              <TD>50 per hour</TD>
              <TD>1,000</TD>
              <TD>Hourly and daily caps enforced</TD>
              <TD>
                <Partial />
              </TD>
            </TR>
          </TBody>
        </Table>

        <H3>What Each Rate Limit Actually Allows</H3>
        <P>
          Rate limits only matter in relation to what you are trying to build.
          The table below translates each free tier limit into the concrete
          workloads it can and cannot support.
        </P>

        <Table>
          <THead>
            <TR>
              <TH>Workload</TH>
              <TH>AxionQuant</TH>
              <TH>Alpha Vantage</TH>
              <TH>Finnhub</TH>
              <TH>Twelve Data</TH>
              <TH>Polygon.io</TH>
              <TH>Tiingo</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>Fetch 10 tickers of daily data</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial>2 min</Partial>
              </TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>Backtest 30 stocks, 1 year daily</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No>2 days</No>
              </TD>
              <TD>
                <Partial>No OHLCV</Partial>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial>6 min</Partial>
              </TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>Backtest 500 stocks, 5 years daily</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No>20 days</No>
              </TD>
              <TD>
                <No>No OHLCV</No>
              </TD>
              <TD>
                <Partial>Multiple days</Partial>
              </TD>
              <TD>
                <Partial>Hours</Partial>
              </TD>
              <TD>
                <Partial>Multiple days</Partial>
              </TD>
            </TR>
            <TR>
              <TD>Real-time dashboard, 20 symbols</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial>25 calls/day insufficient</Partial>
              </TD>
              <TD>
                <Partial>15-min delay</Partial>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No>15-min delay</No>
              </TD>
              <TD>
                <Partial>Limited</Partial>
              </TD>
            </TR>
            <TR>
              <TD>LLM agent making 10 tool calls</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No>Exhausts daily quota</No>
              </TD>
              <TD>
                <Partial>Free tier limited</Partial>
              </TD>
              <TD>
                <Partial>Credits consumed</Partial>
              </TD>
              <TD>
                <No>No MCP</No>
              </TD>
              <TD>
                <No>No MCP</No>
              </TD>
            </TR>
          </TBody>
        </Table>
      </Section>

      <Section>
        <H2 className="serif">Data Quality on Free Tiers</H2>
        <P>
          Free tiers vary not just in how much data they give you, but in how
          reliable that data is. Independent testing has surfaced significant
          quality differences across providers, particularly around corporate
          action adjustments and intraday data.
        </P>

        <P>
          Alpha Vantage free tier has been found to match reference sources on
          approximately 91 to 98 percent of data points in independent testing,
          with errors concentrated in adjusted close prices for stocks that had
          complex corporate actions such as spinoffs and special
          dividends[reference:0]. The free tier also returns a 15-minute delay on
          stock data, and the compact output mode returns only the last 100
          data points by default[reference:1].
        </P>

        <P>
          Finnhub free tier provides real-time US stock quotes with a 15-minute
          delay, but historical OHLCV data is not available at all on the free
          plan. The free tier also has coverage gaps for smaller caps and OTC
          stocks, and news data on the free tier has limited historical
          coverage and restricted outlet diversity[reference:2].
        </P>

        <P>
          Polygon.io free tier delivers end-of-day data only with 2 years of
          historical depth and a 15-minute delay on quotes[reference:3]. Index
          tickers such as VIX and TNX are not available on the free tier at
          all[reference:4].
        </P>

        <P>
          Tiingo free tier provides adjusted historical equity data with strong
          adjustment handling, but intraday depth is limited and the free plan
          does not include access to the fundamental data API or the Tiingo
          News API[reference:5].
        </P>

        <P>
          Twelve Data free tier includes real-time data for US markets, forex,
          and cryptocurrencies, with 800 API credits per day. However,
          fundamentals sit in higher Pro and Ultra tiers, so fundamental data
          is not available on the free plan[reference:6].
        </P>

        <Chart>
          <ChartTitle>
            <BarChart3 size={16} />
            Data Quality Signals by Provider
          </ChartTitle>

          <ChartRow>
            <ChartLabel>AxionQuant</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={100} $color="#0066cc" />
            </ChartBarTrack>
            <ChartValue>Verified</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Tiingo</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={88} $color="#16a34a" />
            </ChartBarTrack>
            <ChartValue>Strong</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Polygon.io</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={85} $color="#0ea5e9" />
            </ChartBarTrack>
            <ChartValue>Good</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Twelve Data</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={78} $color="#f59e0b" />
            </ChartBarTrack>
            <ChartValue>Good</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={72} $color="#f59e0b" />
            </ChartBarTrack>
            <ChartValue>Fair</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Alpha Vantage</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={60} $color="#dc2626" />
            </ChartBarTrack>
            <ChartValue>Variable</ChartValue>
          </ChartRow>
        </Chart>

        <Table>
          <THead>
            <TR>
              <TH>Quality Signal</TH>
              <TH>AxionQuant</TH>
              <TH>Alpha Vantage</TH>
              <TH>Finnhub</TH>
              <TH>Twelve Data</TH>
              <TH>Polygon.io</TH>
              <TH>Tiingo</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>Independent OHLCV match rate</strong>
              </TD>
              <TD>Verified against primary feeds</TD>
              <TD>91 to 98 percent observed</TD>
              <TD>Not applicable, no OHLCV</TD>
              <TD>Not publicly tested</TD>
              <TD>100 percent observed</TD>
              <TD>Strong adjustment handling</TD>
            </TR>
            <TR>
              <TD>
                <strong>Corporate action handling</strong>
              </TD>
              <TD>Handled at ingestion</TD>
              <TD>Timing differences observed</TD>
              <TD>Standard</TD>
              <TD>Standard</TD>
              <TD>Consistent</TD>
              <TD>Strong</TD>
            </TR>
            <TR>
              <TD>
                <strong>Data freshness on free tier</strong>
              </TD>
              <TD>Real-time and historical</TD>
              <TD>15-min delay</TD>
              <TD>15-min delay</TD>
              <TD>Real-time</TD>
              <TD>15-min delay, EOD only</TD>
              <TD>Limited real-time</TD>
            </TR>
            <TR>
              <TD>
                <strong>Known quality issues</strong>
              </TD>
              <TD>None reported</TD>
              <TD>Intraday filtering required</TD>
              <TD>Small cap coverage gaps</TD>
              <TD>Credit system complexity</TD>
              <TD>Index tickers unavailable</TD>
              <TD>Limited intraday</TD>
            </TR>
            <TR>
              <TD>
                <strong>Default history window</strong>
              </TD>
              <TD>Full range by default</TD>
              <TD>100 data points</TD>
              <TD>Not applicable</TD>
              <TD>Full range</TD>
              <TD>2 years</TD>
              <TD>Full range</TD>
            </TR>
          </TBody>
        </Table>

        <Callout>
          <strong>Why adjustment errors matter:</strong> A one-cent difference
          in an adjusted close price may seem trivial, but it compounds over
          time. A backtest that relies on adjusted prices for split and
          dividend calculations will produce different results depending on
          which provider you use. For research that needs to be reproducible,
          data quality is not a secondary concern.
        </Callout>
      </Section>

      <Section>
        <H2 className="serif">Hidden Costs of Free Tiers</H2>
        <P>
          Free tiers have costs beyond the obvious ones. The time you spend
          working around rate limits, the engineering effort required to
          handle inconsistent data, and the eventual upgrade price all factor
          into the true cost of a free plan.
        </P>

        <H3>The Engineering Cost</H3>
        <P>
          Every workaround you write for a free tier limitation is code you
          have to maintain. Caching layers, retry logic, exponential backoff,
          and data normalization all add up. A free tier that saves you $50
          per month but requires 20 hours of engineering work to use
          effectively is not actually free.
        </P>

        <Chart>
          <ChartTitle>
            <Code2 size={16} />
            Engineering Work Required to Use Free Tier
          </ChartTitle>

          <ChartRow>
            <ChartLabel>AxionQuant</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={15} $color="#0066cc" />
            </ChartBarTrack>
            <ChartValue>Minimal</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Tiingo</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={25} $color="#16a34a" />
            </ChartBarTrack>
            <ChartValue>Low</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Twelve Data</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={35} $color="#0ea5e9" />
            </ChartBarTrack>
            <ChartValue>Moderate</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={45} $color="#f59e0b" />
            </ChartBarTrack>
            <ChartValue>Moderate</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Polygon.io</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={55} $color="#f59e0b" />
            </ChartBarTrack>
            <ChartValue>High</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Alpha Vantage</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={85} $color="#dc2626" />
            </ChartBarTrack>
            <ChartValue>Very high</ChartValue>
          </ChartRow>
        </Chart>

        <Table>
          <THead>
            <TR>
              <TH>Hidden Cost</TH>
              <TH>AxionQuant</TH>
              <TH>Alpha Vantage</TH>
              <TH>Finnhub</TH>
              <TH>Twelve Data</TH>
              <TH>Polygon.io</TH>
              <TH>Tiingo</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>Credit card required</strong>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Commercial use restricted</strong>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial>Contact required</Partial>
              </TD>
              <TD>
                <Yes>Personal only</Yes>
              </TD>
              <TD>
                <Partial>Limited</Partial>
              </TD>
              <TD>
                <Partial>Limited</Partial>
              </TD>
              <TD>
                <Yes>Personal only</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Attribution required</strong>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>On free plan</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Historical data gated</strong>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>No OHLCV</Yes>
              </TD>
              <TD>
                <Partial />
              </TD>
              <TD>
                <Yes>2 years only</Yes>
              </TD>
              <TD>
                <No />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Alternative data gated</strong>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes>News API</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Upgrade path cost</strong>
              </TD>
              <TD>Transparent tiers</TD>
              <TD>$49.99 per month</TD>
              <TD>$49.99 per month</TD>
              <TD>$29 per month</TD>
              <TD>$29 per month</TD>
              <TD>$30 per month</TD>
            </TR>
          </TBody>
        </Table>

        <H3>Upgrade Cost Over Time</H3>
        <P>
          The real cost of a free tier is what you pay when you outgrow it.
          The table below shows cumulative spend at the entry paid tier over
          common time horizons.
        </P>

        <Table>
          <THead>
            <TR>
              <TH>Timeframe</TH>
              <TH>AxionQuant</TH>
              <TH>Alpha Vantage</TH>
              <TH>Finnhub</TH>
              <TH>Twelve Data</TH>
              <TH>Polygon.io</TH>
              <TH>Tiingo</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>1 month</TD>
              <TD>Free tier available</TD>
              <TD>$49.99</TD>
              <TD>$49.99</TD>
              <TD>$29</TD>
              <TD>$29</TD>
              <TD>$30</TD>
            </TR>
            <TR>
              <TD>6 months</TD>
              <TD>Free tier available</TD>
              <TD>$299.94</TD>
              <TD>$299.94</TD>
              <TD>$174</TD>
              <TD>$174</TD>
              <TD>$180</TD>
            </TR>
            <TR>
              <TD>12 months</TD>
              <TD>Transparent upgrades</TD>
              <TD>$599.88</TD>
              <TD>$599.88</TD>
              <TD>$348</TD>
              <TD>$348</TD>
              <TD>$360</TD>
            </TR>
            <TR>
              <TD>24 months</TD>
              <TD>Scale when needed</TD>
              <TD>$1,199.76</TD>
              <TD>$1,199.76</TD>
              <TD>$696</TD>
              <TD>$696</TD>
              <TD>$720</TD>
            </TR>
          </TBody>
        </Table>

        <Callout>
          <strong>The legal risk:</strong> Some free APIs scrape data from
          public websites or use undocumented sources without permission from
          the original providers. This can violate terms of service, copyright
          laws, and exchange licensing agreements. If your application depends
          on data from an unlicensed source, you may be exposed to legal risk
          regardless of what the free tier advertises. AxionQuant sources its
          data through proper licensing channels, which removes this concern.
        </Callout>
      </Section>

      <Section>
        <H2 className="serif">Historical Depth on Free Tiers</H2>
        <P>
          Historical depth determines what you can backtest and how far back
          your research can go. Free tiers vary dramatically here, and the
          differences are not always obvious from the marketing pages.
        </P>

        <TimelineContainer>
          <ChartTitle>
            <Clock size={16} />
            Historical Data Depth on Free Tiers
          </ChartTitle>

          <TimelineRow>
            <TimelineLabel>AxionQuant</TimelineLabel>
            <TimelineTrack>
              <TimelineFill $width={100} $color="#0066cc">
                Deep multi-decade
              </TimelineFill>
            </TimelineTrack>
          </TimelineRow>

          <TimelineRow>
            <TimelineLabel>Tiingo</TimelineLabel>
            <TimelineTrack>
              <TimelineFill $width={85} $color="#16a34a">
                Decades of EOD
              </TimelineFill>
            </TimelineTrack>
          </TimelineRow>

          <TimelineRow>
            <TimelineLabel>Alpha Vantage</TimelineLabel>
            <TimelineTrack>
              <TimelineFill $width={70} $color="#f59e0b">
                20+ years daily
              </TimelineFill>
            </TimelineTrack>
          </TimelineRow>

          <TimelineRow>
            <TimelineLabel>Twelve Data</TimelineLabel>
            <TimelineTrack>
              <TimelineFill $width={40} $color="#f59e0b">
                Limited history
              </TimelineFill>
            </TimelineTrack>
          </TimelineRow>

          <TimelineRow>
            <TimelineLabel>Polygon.io</TimelineLabel>
            <TimelineTrack>
              <TimelineFill $width={20} $color="#dc2626">
                2 years EOD only
              </TimelineFill>
            </TimelineTrack>
          </TimelineRow>

          <TimelineRow>
            <TimelineLabel>Finnhub</TimelineLabel>
            <TimelineTrack>
              <TimelineFill $width={5} $color="#dc2626">
                Not available
              </TimelineFill>
            </TimelineTrack>
          </TimelineRow>
        </TimelineContainer>

        <Table>
          <THead>
            <TR>
              <TH>Historical Data</TH>
              <TH>AxionQuant</TH>
              <TH>Alpha Vantage</TH>
              <TH>Finnhub</TH>
              <TH>Twelve Data</TH>
              <TH>Polygon.io</TH>
              <TH>Tiingo</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>Daily OHLCV history</strong>
              </TD>
              <TD>Deep multi-decade</TD>
              <TD>20+ years</TD>
              <TD>
                <No>Not available</No>
              </TD>
              <TD>
                <Partial>Limited</Partial>
              </TD>
              <TD>
                <Partial>2 years</Partial>
              </TD>
              <TD>
                <Yes>Decades of EOD</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Intraday OHLCV history</strong>
              </TD>
              <TD>Deep coverage</TD>
              <TD>
                <Partial>Roughly 2 years</Partial>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial>Limited</Partial>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial>Limited</Partial>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Default history window</strong>
              </TD>
              <TD>Full range by default</TD>
              <TD>100 data points</TD>
              <TD>
                <No>N/A</No>
              </TD>
              <TD>Full range</TD>
              <TD>2 years max</TD>
              <TD>Full range
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Tick data history</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Alternative data history</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
            </TR>
          </TBody>
        </Table>
      </Section>

      <Section>
        <H2 className="serif">Who Should Choose Which</H2>
        <P>
          No single free tier is best for every use case. The table below maps
          common scenarios to the provider that fits best.
        </P>

        <Table>
          <THead>
            <TR>
              <TH>Use Case</TH>
              <TH>Recommended Free Tier</TH>
              <TH>Why</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <Search
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                Learning the basics of financial APIs
              </TD>
              <TD>Alpha Vantage or Finnhub</TD>
              <TD>
                Simple REST APIs with good documentation, low barrier to entry
              </TD>
            </TR>
            <TR>
              <TD>
                <TrendingUp
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                Backtesting with historical data
              </TD>
              <TD>AxionQuant or Tiingo</TD>
              <TD>
                Only free tiers with meaningful historical OHLCV coverage
              </TD>
            </TR>
            <TR>
              <TD>
                <Zap
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                Real-time dashboard or alerts
              </TD>
              <TD>Twelve Data or Finnhub</TD>
              <TD>
                Real-time quotes available on free tier
              </TD>
            </TR>
            <TR>
              <TD>
                <Bot
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                LLM agent with financial tools
              </TD>
              <TD>AxionQuant</TD>
              <TD>
                100+ MCP tools, agent-safe rate limits, free tier access
              </TD>
            </TR>
            <TR>
              <TD>
                <FileText
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                SEC filings and alternative data
              </TD>
              <TD>AxionQuant</TD>
              <TD>
                Only free tier with SEC, insider, ESG, and credit data
              </TD>
            </TR>
            <TR>
              <TD>
                <Scale
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                Commercial application
              </TD>
              <TD>AxionQuant</TD>
              <TD>
                Commercial licensing available on free tier, unlike most
                providers
              </TD>
            </TR>
            <TR>
              <TD>
                <Globe
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                International coverage
              </TD>
              <TD>Finnhub or Twelve Data</TD>
              <TD>
                Both offer some global coverage, though quality varies
              </TD>
            </TR>
            <TR>
              <TD>
                <RefreshCw
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                High-volume data collection
              </TD>
              <TD>Finnhub or Tiingo</TD>
              <TD>
                Per-minute limits allow sustained collection without daily
                caps
              </TD>
            </TR>
          </TBody>
        </Table>
      </Section>

      <Section>
        <H2 className="serif">Verdict</H2>
        <P>
          Free tiers are not created equal. Some are genuinely useful for
          real development work. Others are demo-tier plans that break the
          moment you try to build anything substantial.
        </P>
        <P>
          Alpha Vantage remains the most recognizable name in free financial
          data, but its 25-call daily limit makes it unsuitable for any
          workload that involves more than a handful of tickers. It works
          for learning the basics, not for building.
        </P>
        <P>
          Finnhub offers the most generous per-minute limits and the best
          real-time access on a free tier, but the absence of historical
          OHLCV data is a fundamental limitation for backtesting or any
          research that needs to look backward.
        </P>
        <P>
          Polygon.io free tier is a demo tier in practice. Two years of EOD
          data and 5 requests per minute is enough to evaluate the API, not
          to build on it.
        </P>
        <P>
          Tiingo and Twelve Data occupy a middle ground. Tiingo offers
          excellent EOD history and generous daily limits, but limited
          intraday and no alternative data. Twelve Data provides real-time
          data and broad asset coverage, but fundamentals are gated behind
          paid tiers.
        </P>
        <P>
          AxionQuant is the only free tier in this comparison that provides
          real-time data, deep historical coverage, alternative data, and
          commercial licensing without requiring an upgrade. It offers:
        </P>

        <VerdictBox>
          <ul>
            <li>
              <strong>Real free-tier utility</strong> with real-time and
              historical data across every asset class
            </li>
            <li>
              <strong>Deep alternative data</strong> including SEC filings,
              insider trading, ESG, credit ratings, and sentiment, all
              available from the free tier
            </li>
            <li>
              <strong>Commercial licensing</strong> on the free tier, which
              no other provider in this comparison offers without
              negotiation
            </li>
            <li>
              <strong>An MCP server with 100+ tools</strong> that turns any
              LLM into a financial analyst with live data
            </li>
            <li>
              <strong>Standard rate limit headers</strong> so your
              application can adapt instead of breaking
            </li>
            <li>
              <strong>No credit card required</strong> and no hidden
              restrictions on how you use the data
            </li>
          </ul>
        </VerdictBox>

        <P>
          If you are evaluating free stock data APIs for a project that needs
          to go beyond the prototype stage, AxionQuant is the free tier that
          does not force you to choose between cost and capability.
        </P>

        <CTALink href="https://axionquant.com/dashboard/api-keys">
          Start with a free API key
        </CTALink>
      </Section>

      <Section>
        <H2 className="serif">FAQ</H2>
        <FAQ
          faqs={[
            {
              question: 'Which free stock data API has the best rate limits?',
              answer:
                'Tiingo offers the highest daily limit at 1,000 requests per day, followed by Twelve Data at 800 per day. Finnhub offers 60 requests per minute with no daily cap, which allows sustained collection. AxionQuant offers a generous daily budget designed for real development work. Alpha Vantage has the most restrictive free tier at 25 requests per day, which is insufficient for backtesting or multi-ticker workloads.',
            },
            {
              question: 'Do any free stock data APIs include historical data?',
              answer:
                'Yes. AxionQuant, Alpha Vantage, and Tiingo all include historical OHLCV data on their free tiers. AxionQuant offers deep multi-decade coverage, Alpha Vantage provides 20+ years of daily data, and Tiingo offers decades of EOD data. Finnhub free tier does not include historical OHLCV at all. Polygon.io free tier includes only 2 years of end-of-day data. Twelve Data offers limited historical depth on the free plan.',
            },
            {
              question: 'Can I use free stock data APIs for commercial projects?',
              answer:
                'It depends on the provider. AxionQuant offers commercial licensing on its free tier. Alpha Vantage requires contacting them for commercial use. Finnhub terms specify personal use on all tiers, including paid plans. Tiingo free plan is for personal use with attribution required. Polygon.io and Twelve Data have limited commercial use on free tiers. If your project generates revenue, verify the licensing terms before building on a free tier.',
            },
            {
              question: 'Do free stock data APIs require a credit card?',
              answer:
                'None of the providers in this comparison require a credit card for their free tiers. AxionQuant, Alpha Vantage, Finnhub, Twelve Data, Polygon.io, and Tiingo all allow you to sign up and get an API key without entering payment information.',
            },
            {
              question:
                'What is the best free stock data API for backtesting?',
              answer:
                'AxionQuant and Tiingo are the strongest free options for backtesting because they include meaningful historical OHLCV coverage. Alpha Vantage provides historical data but its 25-call daily limit makes multi-ticker backtests impractical. Finnhub free tier does not include historical OHLCV at all. Polygon.io free tier caps historical depth at 2 years, which limits the strategies you can test.',
            },
            {
              question:
                'Are free stock data APIs reliable enough for production use?',
              answer:
                'Reliability varies significantly. AxionQuant provides verified data with standard rate limit headers and commercial licensing, making it suitable for production. Tiingo offers strong adjustment handling for EOD data. Alpha Vantage has documented data quality inconsistencies, particularly around corporate action adjustments. Finnhub free tier lacks historical data entirely. For production applications, data quality and licensing terms are as important as rate limits.',
            },
            {
              question:
                'Which free stock data API has the best real-time data?',
              answer:
                'Twelve Data and Finnhub offer real-time quotes on their free tiers, though both have limitations. Twelve Data provides real-time data for US markets, forex, and crypto with 800 daily requests. Finnhub provides real-time US quotes with a 15-minute delay and 60 requests per minute. AxionQuant also includes real-time data on its free tier across all asset classes. Alpha Vantage free tier has a 15-minute delay and only 25 daily requests.',
            },
            {
              question:
                'Do any free stock data APIs offer alternative data?',
              answer:
                'AxionQuant is the only provider in this comparison that offers alternative data on its free tier, including SEC filings, insider trading, ESG ratings, credit ratings, and sentiment. Finnhub offers alternative data such as congressional trading and ESG scores, but only on paid plans starting at $49.99 per month. Other providers do not offer alternative data at any tier, or only on enterprise plans.',
            },
            {
              question:
                'What happens when I exceed a free tier rate limit?',
              answer:
                'Behavior varies by provider. AxionQuant reports remaining budget through standard X-RateLimit headers, allowing your application to adapt. Alpha Vantage returns an error payload that may contain a mix of data and upgrade messaging, which can break parsers. Finnhub returns HTTP 429 and asks you to wait 1 to 2 seconds. Polygon.io and Tiingo enforce hard caps. Understanding overage behavior before you build is important for production reliability.',
            },
            {
              question:
                'How do free stock data APIs compare to paid tiers?',
              answer:
                'Free tiers are typically 5 to 20 percent of the capacity of the entry paid tier. Alpha Vantage free tier offers 25 daily requests versus 75 per minute on the $49.99 paid plan. Polygon.io free tier offers 5 requests per minute versus unlimited on the $29 Starter plan. AxionQuant offers a free tier that covers real development work, with transparent paid upgrades when you need higher volume. The key question is whether the free tier lets you validate your idea before you commit to a paid plan.',
            },
          ]}
          schema
        />
      </Section>
    </>
  );
}
